export type TemplateColorRole =
  | "accent"
  | "badge"
  | "band"
  | "bullet"
  | "circle"
  | "date"
  | "decoration"
  | "detail"
  | "divider"
  | "dots"
  | "fourthAccent"
  | "frame"
  | "heading"
  | "icon"
  | "itemTitle"
  | "lightDetails"
  | "lightText"
  | "line"
  | "name"
  | "panel"
  | "paper"
  | "ring"
  | "secondaryAccent"
  | "secondaryText"
  | "sidebar"
  | "sidebarBadge"
  | "sidebarLine"
  | "sidebarText"
  | "text"
  | "thirdAccent"
  | "timeline"
  | "track"
  | "wave";

export type TemplateColor = { key: string; color: string; role: TemplateColorRole };

const colors = (entries: [string, TemplateColorRole][]): TemplateColor[] => [
  { key: "paper", color: "#ffffff", role: "paper" },
  ...entries.map(([key, role]) => ({ key, role, color: `#${key}` })),
];

export const templateColors: Record<string, TemplateColor[]> = {
  "sand-profile": colors([
    ["916b57", "accent"],
    ["f2e9de", "sidebar"],
    ["252525", "text"],
  ]),
  "editorial-muse": colors([
    ["bea68e", "sidebar"],
    ["5d5148", "secondaryText"],
    ["000000", "text"],
  ]),
  "slate-ledger": colors([
    ["353d4d", "sidebar"],
    ["4d5a74", "band"],
    ["444c54", "heading"],
    ["363636", "text"],
    ["e7ece8", "secondaryText"],
    ["edf0eb", "lightText"],
  ]),
  "blue-horizon": colors([
    ["456695", "accent"],
    ["d7dee8", "sidebar"],
    ["202020", "text"],
    ["ffffff", "lightDetails"],
  ]),
  "dune-flow": colors([
    ["cab9a8", "accent"],
    ["d2c1a7", "band"],
    ["e8decd", "sidebar"],
    ["d9d2ca", "line"],
    ["423b36", "heading"],
    ["5d5148", "text"],
    ["000000", "text"],
  ]),
  "navy-summit": colors([
    ["304556", "accent"],
    ["354c5c", "heading"],
    ["d9c4a3", "line"],
    ["252525", "text"],
    ["ffffff", "lightDetails"],
  ]),
  "silver-ledger": colors([
    ["d6e0e4", "band"],
    ["e1e6e8", "line"],
    ["252525", "text"],
  ]),
  "royal-line": colors([
    ["004bac", "accent"],
    ["005197", "heading"],
    ["252525", "text"],
    ["ffffff", "lightDetails"],
  ]),
  "cobalt-balance": colors([
    ["004bac", "accent"],
    ["005197", "heading"],
    ["303030", "text"],
  ]),
  "charcoal-monogram": colors([
    ["c5b985", "accent"],
    ["30302f", "sidebar"],
    ["353332", "text"],
    ["4d4d4a", "secondaryText"],
    ["d9d6d6", "lightText"],
  ]),
  "linen-ledger": colors([
    ["d7cbbb", "accent"],
    ["383838", "text"],
    ["656565", "secondaryText"],
  ]),
  "mono-content": colors([["272727", "text"]]),
  "plum-timeline": colors([
    ["552745", "accent"],
    ["f1eef3", "sidebar"],
    ["30302f", "text"],
    ["ffffff", "lightDetails"],
  ]),
  "citrus-bloom": colors([
    ["f18f50", "accent"],
    ["f6c4d9", "secondaryAccent"],
    ["ffc865", "thirdAccent"],
    ["dfecc7", "panel"],
    ["adc68c", "fourthAccent"],
    ["f9c6a5", "band"],
    ["fcebf1", "sidebar"],
    ["242d29", "text"],
    ["29332f", "secondaryText"],
    ["ffffff", "lightDetails"],
  ]),
  "blue-wave": colors([
    ["1c4165", "sidebar"],
    ["18233d", "decoration"],
    ["6eb2b5", "band"],
    ["d3cede", "circle"],
    ["85bdb5", "wave"],
    ["326a65", "dots"],
    ["011030", "name"],
    ["70a197", "accent"],
    ["303030", "text"],
    ["2e3163", "heading"],
    ["31334a", "itemTitle"],
    ["8faaa3", "date"],
    ["999999", "secondaryText"],
    ["dadada", "line"],
    ["d2eafd", "lightText"],
    ["4f6890", "sidebarLine"],
    ["6db2b3", "sidebarBadge"],
    ["a99bc4", "badge"],
    ["ffffff", "lightDetails"],
  ]),
  "project-flow": colors([
    ["0a1c40", "accent"],
    ["e0601c", "secondaryAccent"],
    ["ea8a55", "line"],
    ["faf9f7", "sidebar"],
    ["2f2f2f", "text"],
    ["3d3d3d", "secondaryText"],
    ["4d4c4c", "date"],
    ["26304a", "itemTitle"],
    ["d5d4d6", "track"],
    ["181818", "bullet"],
    ["292929", "heading"],
    ["353535", "detail"],
    ["363535", "detail"],
    ["4a4a4a", "detail"],
    ["4f4d4e", "detail"],
    ["616161", "detail"],
  ]),
  "fresh-start": colors([
    ["1b1a24", "name"],
    ["5c4596", "heading"],
    ["6e4ca2", "accent"],
    ["2f5a50", "secondaryAccent"],
    ["e2efe8", "panel"],
    ["f0edf5", "sidebar"],
    ["9fd1bb", "ring"],
    ["a59bd0", "line"],
    ["38383c", "text"],
    ["46464a", "secondaryText"],
    ["d3d3d3", "detail"],
    ["ececec", "divider"],
    ["dcdce0", "timeline"],
    ["202025", "itemTitle"],
    ["222222", "bullet"],
    ["2f2f34", "detail"],
    ["333337", "detail"],
    ["3a3a40", "detail"],
    ["58585b", "detail"],
    ["5a5a5d", "detail"],
    ["5f4a95", "detail"],
    ["6650a0", "detail"],
    ["7c66b0", "detail"],
    ["8c8c8f", "detail"],
    ["ffffff", "lightDetails"],
  ]),
  "legal-distinction": colors([
    ["141418", "name"],
    ["5c1420", "accent"],
    ["8a7e7e", "line"],
    ["7a4448", "timeline"],
    ["3a3a3a", "text"],
    ["454545", "sidebarText"],
    ["4f4f50", "secondaryText"],
    ["1c1c1c", "bullet"],
    ["1f1f1f", "itemTitle"],
    ["2c2c2c", "detail"],
    ["444444", "detail"],
  ]),
  "fiscal-arch": colors([
    ["9b3d3d", "accent"],
    ["f1f1f1", "band"],
    ["262322", "heading"],
    ["1f1d1c", "itemTitle"],
    ["3d3a39", "date"],
    ["121110", "text"],
    ["262423", "secondaryText"],
    ["80393a", "line"],
    ["231c1b", "icon"],
    ["ffffff", "lightDetails"],
  ]),
  "soft-contour": colors([
    ["f1ded5", "sidebar"],
    ["fff5f0", "band"],
    ["8d5a41", "accent"],
    ["965c3f", "badge"],
    ["45332a", "itemTitle"],
    ["5a524d", "text"],
    ["5e5853", "secondaryText"],
    ["5f483d", "sidebarText"],
    ["3b2416", "bullet"],
    ["8a6b5d", "line"],
    ["ffffff", "lightDetails"],
  ]),
  "brand-mocha": colors([
    ["37281f", "sidebar"],
    ["a57e65", "accent"],
    ["ddd6cf", "lightText"],
    ["232327", "heading"],
    ["3b3b3b", "itemTitle"],
    ["4f4f4f", "date"],
    ["545454", "text"],
    ["5f5f5f", "bullet"],
    ["4d4e51", "secondaryText"],
    ["34261d", "icon"],
    ["c1c2c4", "track"],
    ["b9a796", "line"],
    ["c4ab9b", "band"],
    ["ddd6d0", "sidebarLine"],
    ["3d3e42", "detail"],
    ["3e4045", "detail"],
    ["6a6b6e", "detail"],
    ["efebe7", "panel"],
    ["f4eee7", "ring"],
    ["ffffff", "lightDetails"],
  ]),
  "forest-frame": colors([
    ["2e422d", "accent"],
    ["195312", "heading"],
    ["1d501a", "itemTitle"],
    ["202020", "text"],
    ["38642c", "icon"],
    ["3c4e37", "secondaryText"],
    ["4c5c47", "detail"],
    ["4e5f4a", "detail"],
    ["d6ded2", "line"],
    ["e2e9dc", "panel"],
    ["eceee8", "sidebar"],
    ["f4f4f0", "band"],
  ]),
  "azure-frame": colors([
    ["243442", "accent"],
    ["384551", "text"],
    ["74868c", "date"],
    ["8b9ca9", "line"],
    ["8fbff2", "secondaryAccent"],
    ["93c6f4", "band"],
    ["cedbdf", "track"],
    ["d0e6ff", "panel"],
    ["d6eaf9", "sidebar"],
    ["d8edfb", "detail"],
    ["f2f8f9", "detail"],
    ["ffffff", "lightDetails"],
  ]),
  "sage-orbit": colors([
    ["174d42", "accent"],
    ["283027", "heading"],
    ["302821", "name"],
    ["30322f", "text"],
    ["343631", "itemTitle"],
    ["343f38", "sidebarText"],
    ["43413c", "secondaryText"],
    ["51766b", "icon"],
    ["52766b", "line"],
    ["7e766e", "date"],
    ["c4ccc2", "track"],
    ["edeae3", "circle"],
    ["eef4ec", "sidebar"],
    ["ffffff", "lightDetails"],
  ]),
  "code-clarity": colors([
    ["090d18", "name"],
    ["0c2152", "accent"],
    ["0e2a60", "heading"],
    ["233d73", "secondaryAccent"],
    ["232428", "text"],
    ["1d2026", "sidebarText"],
    ["6b7a98", "line"],
    ["8f9cb4", "timeline"],
    ["f2f5fa", "sidebar"],
    ["e5ebf7", "panel"],
    ["111114", "bullet"],
    ["1a1a1d", "detail"],
  ]),
  "marketing-pulse": colors([
    ["441733", "accent"],
    ["5d2846", "band"],
    ["96432d", "secondaryAccent"],
    ["bc5a3f", "thirdAccent"],
    ["dc9a88", "line"],
    ["0d0d0e", "itemTitle"],
    ["222222", "text"],
    ["3f3f3f", "secondaryText"],
    ["d6d6d6", "frame"],
    ["c4c4c4", "dots"],
    ["c4c3c1", "track"],
    ["1b1b1b", "bullet"],
    ["ffffff", "lightDetails"],
  ]),
  "people-first": colors([
    ["1c3a1f", "accent"],
    ["11291a", "name"],
    ["264421", "badge"],
    ["828e60", "secondaryAccent"],
    ["050505", "heading"],
    ["191a1a", "text"],
    ["1f3a24", "itemTitle"],
    ["384337", "secondaryText"],
    ["c4c7b5", "line"],
    ["a9ad94", "sidebarLine"],
    ["b8baa9", "divider"],
    ["a3a78f", "ring"],
    ["babdaa", "timeline"],
    ["f0f2e7", "panel"],
    ["e3e6d3", "icon"],
    ["ffffff", "lightDetails"],
  ]),
  "structural-line": colors([
    ["06122b", "accent"],
    ["9e6827", "secondaryAccent"],
    ["6c7383", "line"],
    ["161619", "text"],
    ["f6f6f6", "sidebar"],
    ["c4c4c4", "divider"],
    ["1b1b1d", "bullet"],
  ]),
  "clinical-calm": colors([
    ["104b4e", "accent"],
    ["093a3e", "heading"],
    ["252525", "text"],
    ["80a69f", "secondaryAccent"],
    ["b9ceca", "line"],
    ["bfd2ce", "track"],
    ["dce9e5", "band"],
    ["f7f9f8", "sidebar"],
    ["ffffff", "lightDetails"],
  ]),
  "sales-focus": colors([
    ["073b88", "accent"],
    ["171720", "text"],
    ["454545", "secondaryText"],
    ["bbbbbb", "line"],
    ["c8c8cc", "track"],
    ["d5d5d5", "divider"],
    ["f8f9fb", "sidebar"],
  ]),
  "account-balance": colors([
    ["0a4651", "accent"],
    ["b18f5f", "secondaryAccent"],
    ["d4c4ac", "line"],
    ["222222", "text"],
  ]),
};

export const getTemplateColors = (template: string): TemplateColor[] =>
  templateColors[template] ?? [];
